import React, { useState, useEffect } from 'react';
import './CrosswordPuzzle.css';

interface CrosswordProps {
  onNavigateHome: () => void;
  wordList?: Array<{word: string, clue: string}>;
  onPuzzleComplete?: (timeToComplete?: number) => void; // Made timeToComplete optional since we're removing timer
}

interface Cell {
  letter: string;
  visible: boolean;
  row: number;
  col: number;
  number?: number;
  isStartOfWord: boolean;
  wordDirections: string[];
}

const CrosswordPuzzle: React.FC<CrosswordProps> = ({ 
  onNavigateHome, 
  wordList, 
  onPuzzleComplete
}) => {
  // Default words if none provided
  const defaultWords = [
    { word: 'APPLE', clue: 'A red or green fruit' },
    { word: 'BANANA', clue: 'A long yellow fruit' },
    { word: 'ORANGE', clue: 'A citrus fruit with the same name as its color' },
    { word: 'SCHOOL', clue: 'A place of learning' },
    { word: 'FRIEND', clue: 'Someone you enjoy spending time with' }
  ];

  const words = wordList || defaultWords;
  
  const gridSize = 15;
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [userGrid, setUserGrid] = useState<string[][]>([]);
  const [selectedCell, setSelectedCell] = useState<{row: number, col: number} | null>(null);
  const [currentDirection, setCurrentDirection] = useState<string>('across');
  const [completed, setCompleted] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [clues, setClues] = useState<{across: {number: number, clue: string}[], down: {number: number, clue: string}[]}>({
    across: [],
    down: []
  });

  // Initialize empty grid
  useEffect(() => {
    const initialGrid: Cell[][] = [];
    const initialUserGrid: string[][] = [];
    
    for (let row = 0; row < gridSize; row++) {
      initialGrid[row] = [];
      initialUserGrid[row] = [];
      for (let col = 0; col < gridSize; col++) {
        initialGrid[row][col] = {
          letter: '',
          visible: false,
          row,
          col,
          isStartOfWord: false,
          wordDirections: []
        };
        initialUserGrid[row][col] = '';
      }
    }
    
    // Place words on the grid
    const placedWords = placeWordsOnGrid(initialGrid, words);
    
    if (placedWords.length > 0) {
      setGrid(initialGrid);
      setUserGrid(initialUserGrid);
      
      // Assign numbers to starting cells and create clues
      const { acrossClues, downClues } = generateClues(initialGrid, words, placedWords);
      setClues({
        across: acrossClues,
        down: downClues
      });
    } else {
      setMessage("Couldn't generate crossword with these words. Try different words or refresh.");
    }
  }, [wordList]);

  // Automatically check when puzzle is complete
  useEffect(() => {
    // Guard clause: Only check if grid and userGrid are properly initialized
    if (!grid || !userGrid || grid.length === 0 || userGrid.length === 0) {
      return;
    }
    
    // Check if all visible cells are filled
    let allFilled = true;
    let correctCount = 0;
    let totalCells = 0;
    
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        // Additional safety check for nested arrays
        if (grid[row] && grid[row][col] && grid[row][col].visible) {
          totalCells++;
          if (userGrid[row] && userGrid[row][col]) {
            correctCount++;
            if (userGrid[row][col] !== grid[row][col].letter) {
              allFilled = false;
            }
          } else {
            allFilled = false;
          }
        }
      }
    }
    
    // Only check if all cells are filled
    if (correctCount === totalCells && totalCells > 0) {
      if (allFilled) {
        // All cells are filled and correct - puzzle is complete!
        setCompleted(true);
        setMessage("🎉 Congratulations! You solved the crossword! 🎉");
        
        // Call the onPuzzleComplete callback
        if (onPuzzleComplete) {
          onPuzzleComplete();
        }
      } else {
        // All cells are filled but some are incorrect
        setMessage("Almost there! Check your answers - some might be incorrect.");
        setTimeout(() => setMessage(""), 3000);
      }
    }
  }, [userGrid, grid, onPuzzleComplete]);

  // Function to place words on the grid
  const placeWordsOnGrid = (grid: Cell[][], words: Array<{word: string, clue: string}>) => {
    const placedWords: {word: string, row: number, col: number, direction: string}[] = [];
    const sortedWords = [...words].sort((a, b) => b.word.length - a.word.length);
    
    // Place first word in the middle horizontally
    if (sortedWords.length > 0) {
      const firstWord = sortedWords[0].word;
      const middleRow = Math.floor(gridSize / 2);
      const startCol = Math.floor((gridSize - firstWord.length) / 2);
      
      // Place the first word
      for (let i = 0; i < firstWord.length; i++) {
        grid[middleRow][startCol + i].letter = firstWord[i];
        grid[middleRow][startCol + i].visible = true;
      }
      
      grid[middleRow][startCol].isStartOfWord = true;
      grid[middleRow][startCol].wordDirections.push('across');
      
      placedWords.push({
        word: firstWord,
        row: middleRow,
        col: startCol,
        direction: 'across'
      });
    }
    
    // Try to place remaining words
    for (let i = 1; i < sortedWords.length; i++) {
      const word = sortedWords[i].word;
      let placed = false;
      
      // Try to find intersections with existing words
      for (let attempt = 0; attempt < 100 && !placed; attempt++) {
        for (let letterIdx = 0; letterIdx < word.length && !placed; letterIdx++) {
          const letter = word[letterIdx];
          
          // Look for matching letters in the grid
          for (let row = 0; row < gridSize && !placed; row++) {
            for (let col = 0; col < gridSize && !placed; col++) {
              if (grid[row][col].letter === letter) {
                // Try placing vertically (down)
                if (!grid[row][col].wordDirections.includes('down')) {
                  const startRow = row - letterIdx;
                  if (startRow >= 0 && startRow + word.length <= gridSize) {
                    if (canPlaceWordVertically(grid, word, startRow, col, letterIdx)) {
                      placeWordVertically(grid, word, startRow, col);
                      grid[startRow][col].isStartOfWord = true;
                      grid[startRow][col].wordDirections.push('down');
                      placedWords.push({
                        word,
                        row: startRow,
                        col,
                        direction: 'down'
                      });
                      placed = true;
                      break;
                    }
                  }
                }
                
                // Try placing horizontally (across)
                if (!grid[row][col].wordDirections.includes('across')) {
                  const startCol = col - letterIdx;
                  if (startCol >= 0 && startCol + word.length <= gridSize) {
                    if (canPlaceWordHorizontally(grid, word, row, startCol, letterIdx)) {
                      placeWordHorizontally(grid, word, row, startCol);
                      grid[row][startCol].isStartOfWord = true;
                      grid[row][startCol].wordDirections.push('across');
                      placedWords.push({
                        word,
                        row,
                        col: startCol,
                        direction: 'across'
                      });
                      placed = true;
                      break;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return placedWords;
  };

  // Check if a word can be placed vertically
  const canPlaceWordVertically = (grid: Cell[][], word: string, startRow: number, col: number, intersectIdx: number) => {
    // Check bounds
    if (startRow < 0 || startRow + word.length > gridSize) return false;
    
    // Check for blocking cells above
    if (startRow > 0 && grid[startRow - 1][col].visible) return false;
    
    // Check for blocking cells below
    if (startRow + word.length < gridSize && grid[startRow + word.length][col].visible) return false;
    
    // Check each position
    for (let i = 0; i < word.length; i++) {
      const row = startRow + i;
      
      // If cell is already filled, it must match the letter we're trying to place
      if (grid[row][col].visible && grid[row][col].letter !== word[i]) {
        return false;
      }
      
      // Check adjacent cells (except at intersection)
      if (i !== intersectIdx) {
        if (col > 0 && grid[row][col - 1].visible) return false;
        if (col < gridSize - 1 && grid[row][col + 1].visible) return false;
      }
    }
    
    return true;
  };

  // Check if a word can be placed horizontally
  const canPlaceWordHorizontally = (grid: Cell[][], word: string, row: number, startCol: number, intersectIdx: number) => {
    // Check bounds
    if (startCol < 0 || startCol + word.length > gridSize) return false;
    
    // Check for blocking cells to the left
    if (startCol > 0 && grid[row][startCol - 1].visible) return false;
    
    // Check for blocking cells to the right
    if (startCol + word.length < gridSize && grid[row][startCol + word.length].visible) return false;
    
    // Check each position
    for (let i = 0; i < word.length; i++) {
      const col = startCol + i;
      
      // If cell is already filled, it must match the letter we're trying to place
      if (grid[row][col].visible && grid[row][col].letter !== word[i]) {
        return false;
      }
      
      // Check adjacent cells (except at intersection)
      if (i !== intersectIdx) {
        if (row > 0 && grid[row - 1][col].visible) return false;
        if (row < gridSize - 1 && grid[row + 1][col].visible) return false;
      }
    }
    
    return true;
  };

  // Place a word vertically
  const placeWordVertically = (grid: Cell[][], word: string, startRow: number, col: number) => {
    for (let i = 0; i < word.length; i++) {
      grid[startRow + i][col].letter = word[i];
      grid[startRow + i][col].visible = true;
    }
  };

  // Place a word horizontally
  const placeWordHorizontally = (grid: Cell[][], word: string, row: number, startCol: number) => {
    for (let i = 0; i < word.length; i++) {
      grid[row][startCol + i].letter = word[i];
      grid[row][startCol + i].visible = true;
    }
  };

  // Generate clues for the crossword
  const generateClues = (grid: Cell[][], words: Array<{word: string, clue: string}>, placedWords: Array<{word: string, row: number, col: number, direction: string}>) => {
    const acrossClues: {number: number, clue: string}[] = [];
    const downClues: {number: number, clue: string}[] = [];
    let clueNumber = 1;
    
    // Scan grid for starting cells
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        if (grid[row][col].isStartOfWord) {
          grid[row][col].number = clueNumber;
          
          // Find corresponding words and clues
          const acrossWord = placedWords.find(w => 
            w.direction === 'across' && w.row === row && w.col === col
          );
          
          const downWord = placedWords.find(w => 
            w.direction === 'down' && w.row === row && w.col === col
          );
          
          if (acrossWord) {
            const wordObj = words.find(w => w.word === acrossWord.word);
            if (wordObj) {
              acrossClues.push({
                number: clueNumber,
                clue: wordObj.clue
              });
            }
          }
          
          if (downWord) {
            const wordObj = words.find(w => w.word === downWord.word);
            if (wordObj) {
              downClues.push({
                number: clueNumber,
                clue: wordObj.clue
              });
            }
          }
          
          clueNumber++;
        }
      }
    }
    
    return { acrossClues, downClues };
  };

  // Handle cell click
  const handleCellClick = (row: number, col: number) => {
    if (!grid[row][col].visible) return;
    
    if (selectedCell && selectedCell.row === row && selectedCell.col === col) {
      // Toggle direction if clicking on same cell
      setCurrentDirection(currentDirection === 'across' ? 'down' : 'across');
    } else {
      setSelectedCell({ row, col });
    }
  };

  // Find the next visible cell in the specified direction
  const findNextVisibleCell = (row: number, col: number, direction: string) => {
    if (direction === 'across') {
      // Move right until we find a visible cell or hit the edge
      for (let c = col + 1; c < gridSize; c++) {
        if (grid[row] && grid[row][c] && grid[row][c].visible) {
          return { row, col: c };
        }
      }
    } else {
      // Move down until we find a visible cell or hit the edge
      for (let r = row + 1; r < gridSize; r++) {
        if (grid[r] && grid[r][col] && grid[r][col].visible) {
          return { row: r, col };
        }
      }
    }
    return null;
  };

  // Find the previous visible cell in the specified direction
  const findPrevVisibleCell = (row: number, col: number, direction: string) => {
    if (direction === 'across') {
      // Move left until we find a visible cell or hit the edge
      for (let c = col - 1; c >= 0; c--) {
        if (grid[row] && grid[row][c] && grid[row][c].visible) {
          return { row, col: c };
        }
      }
    } else {
      // Move up until we find a visible cell or hit the edge
      for (let r = row - 1; r >= 0; r--) {
        if (grid[r] && grid[r][col] && grid[r][col].visible) {
          return { row: r, col };
        }
      }
    }
    return null;
  };

  // Handle key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const key = event.key;
    
    if (key.length === 1 && /^[a-zA-Z]$/.test(key)) {
      // Update user grid with uppercase letter
      const newUserGrid = [...userGrid];
      newUserGrid[row][col] = key.toUpperCase();
      setUserGrid(newUserGrid);
      
      // Move to next cell based on current direction
      const nextCell = findNextVisibleCell(row, col, currentDirection);
      if (nextCell) {
        setSelectedCell(nextCell);
      }
    } else if (key === 'Backspace') {
      // Clear current cell
      const newUserGrid = [...userGrid];
      newUserGrid[row][col] = '';
      setUserGrid(newUserGrid);
      
      // Move to previous cell based on current direction
      const prevCell = findPrevVisibleCell(row, col, currentDirection);
      if (prevCell) {
        setSelectedCell(prevCell);
      }
    } else if (key === 'ArrowRight') {
      const nextCell = findNextVisibleCell(row, col, 'across');
      if (nextCell) {
        setSelectedCell(nextCell);
        setCurrentDirection('across');
      }
    } else if (key === 'ArrowLeft') {
      const prevCell = findPrevVisibleCell(row, col, 'across');
      if (prevCell) {
        setSelectedCell(prevCell);
        setCurrentDirection('across');
      }
    } else if (key === 'ArrowUp') {
      const prevCell = findPrevVisibleCell(row, col, 'down');
      if (prevCell) {
        setSelectedCell(prevCell);
        setCurrentDirection('down');
      }
    } else if (key === 'ArrowDown') {
      const nextCell = findNextVisibleCell(row, col, 'down');
      if (nextCell) {
        setSelectedCell(nextCell);
        setCurrentDirection('down');
      }
    } else if (key === 'Tab') {
      event.preventDefault(); // Prevent tabbing out of the grid
      
      // Move to next word's starting cell
      const clueList = currentDirection === 'across' ? clues.across : clues.down;
      if (clueList.length > 0) {
        // Find current number
        let currentNumber = -1;
        for (let r = 0; r < gridSize; r++) {
          for (let c = 0; c < gridSize; c++) {
            if (grid[r][c].number !== undefined && 
                ((currentDirection === 'across' && r === row && c <= col) || 
                (currentDirection === 'down' && c === col && r <= row))) {
              currentNumber = grid[r][c].number!;
            }
          }
        }
        
        // Find next number in the grid
        let nextNumber = -1;
        let nextCell = null;
        
        if (event.shiftKey) {
          // Find previous clue
          for (let i = clueList.length - 1; i >= 0; i--) {
            if (clueList[i].number < currentNumber) {
              nextNumber = clueList[i].number;
              break;
            }
          }
          
          if (nextNumber === -1 && clueList.length > 0) {
            nextNumber = clueList[clueList.length - 1].number;
          }
        } else {
          // Find next clue
          for (let i = 0; i < clueList.length; i++) {
            if (clueList[i].number > currentNumber) {
              nextNumber = clueList[i].number;
              break;
            }
          }
          
          if (nextNumber === -1 && clueList.length > 0) {
            nextNumber = clueList[0].number;
          }
        }
        
        // Find cell with that number
        for (let r = 0; r < gridSize; r++) {
          for (let c = 0; c < gridSize; c++) {
            if (grid[r][c].number !== undefined && grid[r][c].number === nextNumber) {
              nextCell = { row: r, col: c };
              break;
            }
          }
          if (nextCell) break;
        }
        
        if (nextCell) {
          setSelectedCell(nextCell);
        }
      }
    }
  };

 // Modified checkPuzzle function - now mainly for the "Check Answers" button
const checkPuzzle = () => {
  let isCorrect = true;
  let totalFilled = 0;
  let totalCells = 0;
  
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      if (grid[row][col].visible) {
        totalCells++;
        if (userGrid[row][col]) {
          totalFilled++;
        }
        
        if (userGrid[row][col] !== grid[row][col].letter) {
          isCorrect = false;
        }
      }
    }
  }
  
  if (totalFilled < totalCells) {
    setMessage(`You've filled ${totalFilled} out of ${totalCells} cells. Keep going!`);
    setTimeout(() => setMessage(""), 3000);
    return;
  }
  
  if (isCorrect) {
    setCompleted(true);
    setMessage("🎉 Congratulations! You solved the crossword! 🎉");
    
    // Call the onPuzzleComplete callback
    if (onPuzzleComplete) {
      onPuzzleComplete();
    }
  } else {
    setMessage("Not quite right. Keep trying!");
    setTimeout(() => setMessage(""), 3000);
  }
};

  // Reset the puzzle
  const resetPuzzle = () => {
    const newUserGrid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(''));
    setUserGrid(newUserGrid);
    setSelectedCell(null);
    setCompleted(false);
    setMessage('');
  };

  // Show a hint (reveal one letter)
  const showHint = () => {
    if (!selectedCell) {
      setMessage("Select a cell to get a hint");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    
    const { row, col } = selectedCell;
    const newUserGrid = [...userGrid];
    newUserGrid[row][col] = grid[row][col].letter;
    setUserGrid(newUserGrid);
    
    const nextCell = findNextVisibleCell(row, col, currentDirection);
    if (nextCell) {
      setSelectedCell(nextCell);
    }
  };

  // Highlight the current word
  const getHighlightedCells = () => {
    if (!selectedCell || !grid || grid.length === 0) return {};
    
    const highlightedCells: Record<string, boolean> = {};
    const { row, col } = selectedCell;
    
    if (currentDirection === 'across') {
      // Find the start of the word
      let startCol = col;
      while (startCol > 0 && grid[row] && grid[row][startCol - 1] && grid[row][startCol - 1].visible) {
        startCol--;
      }
      
      // Highlight all cells in the word
      let c = startCol;
      while (c < gridSize && grid[row] && grid[row][c] && grid[row][c].visible) {
        highlightedCells[`${row}-${c}`] = true;
        c++;
      }
    } else {
      // Find the start of the word
      let startRow = row;
      while (startRow > 0 && grid[startRow - 1] && grid[startRow - 1][col] && grid[startRow - 1][col].visible) {
        startRow--;
      }
      
      // Highlight all cells in the word
      let r = startRow;
      while (r < gridSize && grid[r] && grid[r][col] && grid[r][col].visible) {
        highlightedCells[`${r}-${col}`] = true;
        r++;
      }
    }
    
    return highlightedCells;
  };

  const highlightedCells = getHighlightedCells();

  return (
    <div className="crossword-container" onKeyDown={handleKeyPress} tabIndex={0}>
      <h2 className="crossword-title">Vocabulary Crossword</h2>
      
      {message && <div className="crossword-message">{message}</div>}
      
      <div className="crossword-grid-container">
        <div className="crossword-grid">
          {grid.map((row, rowIdx) => (
            <div key={`row-${rowIdx}`} className="crossword-row">
              {row.map((cell, colIdx) => (
                <div 
                  key={`cell-${rowIdx}-${colIdx}`} 
                  className={`crossword-cell ${cell.visible ? 'visible' : 'invisible'} ${
                    selectedCell && selectedCell.row === rowIdx && selectedCell.col === colIdx ? 'selected' : ''
                  } ${
                    highlightedCells[`${rowIdx}-${colIdx}`] ? 'highlighted' : ''
                  } ${completed ? 'completed' : ''}`}
                  onClick={() => handleCellClick(rowIdx, colIdx)}
                >
                  {cell.number !== undefined && <span className="cell-number">{cell.number}</span>}
                  {cell.visible && (
                    <span className="cell-letter">
                      {userGrid[rowIdx][colIdx]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        
        <div className="crossword-clues">
          <div className="clues-section">
            <h3>Across</h3>
            <ul>
              {clues.across.map((clue, index) => (
                <li key={`across-${index}`}><strong>{clue.number}.</strong> {clue.clue}</li>
              ))}
            </ul>
          </div>
          
          <div className="clues-section">
            <h3>Down</h3>
            <ul>
              {clues.down.map((clue, index) => (
                <li key={`down-${index}`}><strong>{clue.number}.</strong> {clue.clue}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      <div className="crossword-controls">
        <button className="crossword-btn" onClick={checkPuzzle}>Check Answers</button>
        <button className="crossword-btn" onClick={showHint}>Hint</button>
        <button className="crossword-btn" onClick={resetPuzzle}>Reset</button>
        <button className="crossword-btn" onClick={onNavigateHome}>Go to Home</button>
      </div>
      
      <div className="crossword-instructions">
        <p>• Click a cell to start typing</p>
        <p>• Click again to change direction (across/down)</p>
        <p>• Use arrow keys to navigate between cells</p>
        <p>• Press Tab to move to the next word</p>
      </div>
    </div>
  );
};

export default CrosswordPuzzle;